// import bcrypt from "bcrypt";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import { eq } from "drizzle-orm";
import { useState } from "react";
import { Link, redirect } from "react-router";
import { trpc } from "~/TrpcClient";
import { requireSess } from "~/auth.server";
import { Breadcrumb } from "~/components/ui/Breadcrumb";
import { PageTitle } from "~/components/ui/PageTitle";
import { pg } from "~/pg.server";
import { useRealm, useT, useTurl } from "~/util/Lang";
import { $LOADER, $PAGE, callApi } from "~/util/Loaders";
import css from "./Profile.module.css";
import { useCopy } from "~/util/Copy";
import clsx from "clsx";
import { subscriptions } from "drizzle/schema";
import { Icon } from "~/components/ui/Icon";
import { Button } from "~/components/ui/Button";
import { LinkDiscordButton } from "~/components/site/links/LinkDiscordButton";
import { unlinkDiscordApi } from "../api/auth/discordUnlink.pages";
import { logPageView } from "~/util/stats";

dayjs.extend(timezone);

$PAGE({
  path: "profile",
  loader: $LOADER?.(async (args) => {
    // Log
    logPageView(args);
    //
    const sess = requireSess(args.req.sess);
    const user = await pg.db
      .select({
        login: pg.users.login,
        timezone: pg.users.timezone,
        subscriptionExpiry: pg.subscriptions.endsAt,
        token: subscriptions.key,
        avatar: pg.users.avatar,
        discord: {
          id: pg.userDiscords.discordId,
          username: pg.userDiscords.username,
        },
      })
      .from(pg.users)
      .where(eq(pg.users.id, sess.user.id))
      .leftJoin(pg.subscriptions, eq(pg.subscriptions.userId, pg.users.id))
      .leftJoin(pg.userDiscords, eq(pg.userDiscords.userId, pg.users.id))
      .then((res) => res.at(0));
    if (!user) {
      return redirect("/login");
    }

    return {
      authUser: sess?.user,
      user,
    };
  }),
  meta: () => {
    const title = "Profile";

    return {
      title: `${title} - L2Hub`,
      description: "Profile L2Hub",
    };
  },
  render: (props) => {
    const { data } = props;
    const { user } = data;
    const [token, setToken] = useState(user.token ?? "");
    const [timezone, setTimezone] = useState(user.timezone ?? "");
    const [finished, setFinished] = useState(false);
    const [avatar, setAvatar] = useState(user.avatar ?? "skill4293");
    const t = useT();
    const turl = useTurl();
    const realm = useRealm();
    const title = t({ en: "Profile", ru: "Профиль" });
    const [, copy] = useCopy();

    const profile = trpc.updateTimezone.useMutation();
    const userToken = trpc.updateToken.useMutation();
    const userAvatar = trpc.updateAvatar.useMutation();

    async function doUpdate(type: "timezone" | "avatar") {
      if (type === "timezone") {
        const result = await profile.mutateAsync({ timezone });
        switch (result.t) {
          case "success": {
            setFinished(true);
            break;
          }
        }
      }
      if (type === "avatar") {
        const result = await userAvatar.mutateAsync({ avatar });
        switch (result.t) {
          case "success": {
            setFinished(true);
            break;
          }
        }
      }
    }

    async function doUpdateToken() {
      const result = await userToken.mutateAsync();
      switch (result.t) {
        case "success": {
          setToken(result.data.token);
          setFinished(true);
          break;
        }
      }
    }

    async function doUnlinkDiscrord() {
      const result = await callApi(unlinkDiscordApi, {
        input: { userId: data.authUser.id },
        realm,
      });
      console.log(result);
    }

    const subIsActive = user
      ? dayjs(user.subscriptionExpiry).diff(dayjs()) > 0
      : false;

    return (
      <div className={css.root}>
        <div className="content">
          <Breadcrumb
            noRoot={true}
            items={[{ title, url: turl("/profile") }]}
          />
          <div className="columns">
            <div className="column is-three-quarters">
              <PageTitle>Hi, {data.user.login}</PageTitle>
              <div className={css.block}>
                <div className={css.title}>Link Discord</div>
                <p style={{ fontSize: 11, textTransform: "uppercase" }}>
                  By connecting your Discord account, you can streamline the
                  authentication process.
                </p>
                {user.discord ? (
                  <div>
                    <div style={{ color: " #5865f2", fontSize: 14 }}>
                      @{user.discord.username}
                    </div>
                    {data.authUser.isAdmin && (
                      <div onClick={doUnlinkDiscrord}>Unlink Discord</div>
                    )}
                  </div>
                ) : (
                  <LinkDiscordButton children="Link Discord" disabled={false} />
                )}
              </div>
              <div className={css.block}>
                <div className={css.title}>Avatar</div>
                <p style={{ fontSize: 11, textTransform: "uppercase" }}>
                  choose an avatar:
                </p>
                <div className={css.avatarList}>
                  {[
                    "skill4290",
                    "skill4291",
                    "skill4292",
                    "skill4293",
                    "skill4294",
                    "skill4295",
                    "skill4296",
                    "skill4297",
                    "skill4298",
                    "skill4299",
                    "skill4300",
                    "skill4301",
                    "skill4302",
                  ].map((icon, i) => (
                    <div
                      className={clsx(css.item, {
                        [css.active]: icon === avatar,
                      })}
                      key={i}
                      onClick={() => setAvatar(icon)}
                    >
                      <Icon icon={icon} />
                    </div>
                  ))}
                </div>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    doUpdate("avatar");
                  }}
                >
                  <div style={{ marginTop: 10 }}>
                    <button className={css.button} type="submit">
                      Select Avatar
                    </button>
                  </div>
                </form>
              </div>
              <div className={css.block}>
                <div className={css.title}>Tracker Settings</div>
                <div style={{ position: "relative" }}>
                  {!subIsActive ? (
                    <div className={css.disabled}>Subscription Ended</div>
                  ) : null}
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      doUpdateToken();
                    }}
                  >
                    <p style={{ fontSize: 11, textTransform: "uppercase" }}>
                      The subscription is valid until:{" "}
                      {user.subscriptionExpiry
                        ? dayjs(user.subscriptionExpiry).format("YYYY-MM-DD")
                        : "none"}
                    </p>
                    <div>
                      <label className={css.label} htmlFor="token">
                        Token:
                      </label>
                      <div style={{ display: "flex", gap: 10 }}>
                        <input
                          value={token}
                          id="token"
                          name="token"
                          className={css.input}
                          disabled
                        />
                        <div
                          className={css.inputButton}
                          onClick={() => copy(token)}
                        >
                          Copy
                        </div>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
                      <button className={css.button} type="submit">
                        Generate New Token
                      </button>
                      {DEV && (
                        <Link
                          className={clsx([css.button, css.trackerButton])}
                          to="/tracker"
                        >
                          Tracker
                        </Link>
                      )}
                    </div>
                  </form>
                </div>
              </div>
              <div className={css.block}>
                <div className={css.title}>Time Zone Settings</div>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    doUpdate("timezone");
                  }}
                >
                  <p style={{ fontSize: 11, textTransform: "uppercase" }}>
                    Your Time Zone: {timezone}
                  </p>
                  <div style={{ display: "flex", gap: 10 }}>
                    <button
                      className={css.button}
                      onClick={() => setTimezone(dayjs.tz.guess())}
                    >
                      Update Time Zone
                    </button>
                    {/* <button className={css.button} type="submit">
                    Save Profile
                  </button> */}
                  </div>
                </form>
              </div>
            </div>
            <div className="column">
              <ul>
                <li>{DEV && <Link to="/tracker">Tacker</Link>}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  },
});
